import { useState, useEffect } from 'react';
import { 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Button,
  Switch
} from '@heroui/react';
import { DashboardPreferencesManager, DashboardBlock } from '../../utils/dashboardPreferences';
import { Eye, EyeSlash, ArrowCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'react-toastify';

interface DashboardCustomizerProps {
  isOpen: boolean;
  onClose: () => void;
  onPreferencesChange: () => void;
}

export const DashboardCustomizer = ({ 
  isOpen, 
  onClose, 
  onPreferencesChange 
}: DashboardCustomizerProps) => {
  const [blocks, setBlocks] = useState<DashboardBlock[]>([]);

  useEffect(() => {
    if (isOpen) {
      setBlocks(DashboardPreferencesManager.getPreferences());
    }
  }, [isOpen]);

  const handleToggleBlock = (blockId: string) => {
    const updatedBlocks = blocks.map(block =>
      block.id === blockId ? { ...block, visible: !block.visible } : block
    );
    setBlocks(updatedBlocks);
    DashboardPreferencesManager.savePreferences(updatedBlocks);
    onPreferencesChange();
  };

  const handleReset = () => {
    DashboardPreferencesManager.resetToDefaults();
    setBlocks(DashboardPreferencesManager.getPreferences());
    onPreferencesChange();
    toast.success('Dashboard voorkeuren gereset naar standaard');
  };

  const visibleCount = blocks.filter(b => b.visible).length;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3 className="text-xl font-semibold">Dashboard aanpassen</h3>
          <p className="text-sm text-neutral-secondary-mid font-normal">
            Kies welke blokken je wilt zien op je dashboard
          </p>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-3">
            {blocks.map((block) => (
              <div 
                key={block.id}
                className="flex items-center justify-between p-3 rounded-lg border border-neutral-primary/10 hover:bg-neutral-primary/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {block.visible ? (
                    <Eye size={20} className="text-moss-primary" weight="duotone" />
                  ) : (
                    <EyeSlash size={20} className="text-neutral-secondary-mid" weight="duotone" />
                  )}
                  <div>
                    <p className="font-medium text-neutral-primary">{block.name}</p>
                    <p className="text-xs text-neutral-secondary-mid">
                      {block.visible ? 'Zichtbaar' : 'Verborgen'}
                    </p>
                  </div>
                </div>
                <Switch
                  isSelected={block.visible}
                  onValueChange={() => handleToggleBlock(block.id)}
                  color="success"
                />
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-moss-primary/10 border border-moss-primary/20">
            <div className="flex items-start gap-3">
              <div className="text-moss-primary">
                <Eye size={24} weight="duotone" />
              </div>
              <div>
                <p className="font-medium text-neutral-primary">
                  {visibleCount} van {blocks.length} blokken zichtbaar
                </p>
                <p className="text-sm text-neutral-secondary-mid mt-1">
                  Je voorkeuren worden opgeslagen in je browser. In de toekomst zullen deze worden gesynchroniseerd met je account.
                </p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="flat"
            color="danger"
            startContent={<ArrowCounterClockwise size={18} />}
            onPress={handleReset}
          >
            Reset naar standaard
          </Button>
          <Button
            color="primary"
            onPress={onClose}
          >
            Sluiten
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

